import { createContext, useContext, useState } from 'react'
import { logWarning } from '@/services/errorService'

import { ldClient, getCurrentLocationContext } from '@/lib/launchDarkly'
import { useAppDispatch, useAppSelector } from '@/store'
import { logout as reduxLogout } from '@/store/authSlice'
import type { AuthUser } from '@/store/authSlice'

export type { AuthUser }

export interface LoginCredentials {
	email: string
	password: string
}

interface AuthContextValue {
	isAuthenticated: boolean
	isLoading: boolean
	user: AuthUser | null
	login: (credentials: LoginCredentials) => Promise<void>
	logout: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

interface AuthProviderProps {
	children: React.ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
	const dispatch = useAppDispatch()
	const user = useAppSelector((state) => state.auth.user)
	const [isLoading, setIsLoading] = useState(false)

	// TODO: implement in auth ticket — call the real authentication API and set user state.
	// After obtaining the JWT, dispatch setCredentials({ token, user }) from '@/store/authSlice'
	// so that oocApi and oloApi automatically attach the Bearer token on all subsequent requests.
	const login = async (_credentials: LoginCredentials): Promise<void> => {
		setIsLoading(true)
		try {
			// TODO: replace with real API call
			// const { token, user: newUser } = await oocApi.post('/auth/login', _credentials)
			// dispatch(setCredentials({ token, user: newUser }))

			// After successful login, add the user context to LD alongside the location context.
			// Using multi-context preserves venue-level flag targeting — mirrors STO's pattern
			// of keeping both location and user contexts active simultaneously.
			const locationCtx = getCurrentLocationContext()
			if (ldClient && locationCtx && user) {
				await ldClient.identify({
					kind: 'multi',
					location: locationCtx,
					user: { kind: 'user', key: user.id, email: user.email, name: user.name },
				})
			}
		} finally {
			setIsLoading(false)
		}
	}

	// TODO: implement in auth ticket
	const logout = async (): Promise<void> => {
		dispatch(reduxLogout())

		// On logout, revert to location-only context — remove the user from LD targeting.
		const locationCtx = getCurrentLocationContext()
		if (ldClient && locationCtx) {
			try {
				await ldClient.identify(locationCtx)
			} catch (error) {
				logWarning('[AuthProvider] Failed to revert LaunchDarkly context on logout', {
					error,
				})
			}
		}
	}

	return (
		<AuthContext.Provider
			value={{
				isAuthenticated: user !== null,
				isLoading,
				user,
				login,
				logout,
			}}
		>
			{children}
		</AuthContext.Provider>
	)
}

export function useAuth(): AuthContextValue {
	const ctx = useContext(AuthContext)
	if (!ctx) throw new Error('useAuth must be used within AuthProvider')
	return ctx
}
